import React from 'react';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import Title from '@Components/Title';
import useScroll from '../../useScroll';

interface Props {
  clearanceDocs: string[];
  loading?: boolean;
}

export default function RequiredDocuments({ clearanceDocs, loading }: Props) {
  const { requiredDocumentsRef } = useScroll();

  if (!loading && (!clearanceDocs || clearanceDocs.length === 0)) {
    return null;
  }

  return (
    <>
      <Grid container mt={4} mb={2} ref={requiredDocumentsRef}>
        <Title title="Required Clearance Documents" removeBorder removePadding />
      </Grid>
      <Grid
        container
        className="p-4 mb-3 border border-solid border-light rounded"
        sx={{ bgcolor: 'background.paper' }}
      >
        <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
          {/* Header Description & Count */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              mb: 2.5,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 1.5,
            }}
          >
            <Typography sx={{ fontSize: '13.5px', color: 'text.light' }}>
              The following documents are required for customs clearance and processing of this shipment.
            </Typography>
            {!loading && clearanceDocs.length > 0 && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '16px',
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                  flexShrink: 0,
                }}
              >
                {clearanceDocs.length} {clearanceDocs.length === 1 ? 'Document' : 'Documents'}
              </Box>
            )}
          </Box>

          {/* Document List */}
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2.5 }}>
              <CircularProgress size={20} thickness={4} />
              <Typography sx={{ color: 'text.light', fontSize: '13.5px' }}>
                Fetching required clearance documents...
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                columnGap: 4,
                rowGap: 0,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {clearanceDocs.map((docName, idx) => (
                <Box
                  key={`${docName}-${idx}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1.25,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    minWidth: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                      flexShrink: 0,
                      mr: 1.5,
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: '14px' }} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '13.5px',
                      color: theme => theme.palette.text.dark || '#344054',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                    title={docName}
                  >
                    {docName}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Grid>
    </>
  );
}
